import { observer } from "mobx-react-lite";
import { FC } from "react";

import {
  MdsItemListSkeletonRowData,
  MdsItemListSize,
} from "@/design-system/components/item-list/types";
import { MdsSpacer } from "@/design-system/components/spacer";
import styled from "@emotion/styled";

export interface MdsItemListSkeletonRowProps {
  data: MdsItemListSkeletonRowData;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsItemListSkeletonRow: FC<MdsItemListSkeletonRowProps> = observer(
  ({ data: { size = MdsItemListSize.Medium } }) => {
    return (
      <Wrapper $size={size}>
        <Icon />
        <Content />
        <MdsSpacer />
        <Timestamp />
      </Wrapper>
    );
  }
);

const Wrapper = styled.div<{ $size: MdsItemListSize }>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background.list.default};
  border-radius: 12px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  padding-left: 4px;
  padding-right: 16px;
  transition: 0.08s ease;
  user-select: none;
  width: 100%;
  ${({ $size }) => rowSizeStyleMapping[$size]};

  "&:hover": {
    background-color: ${({ theme }) => theme.colors.background.list.hover};
  },
`;

const Icon = styled.div`
  margin: 0 8px;
  width: 18px;
`;

const Content = styled.div`
  flex-grow: 1;
  margin: 0 2px;
  max-width: 45%;
`;

const Timestamp = styled.div`
  margin: 0;
  width: 61px;
`;

const rowSizeStyleMapping: { [key in MdsItemListSize]: string } = {
  [MdsItemListSize.Medium]: `height: 48px;`,
  [MdsItemListSize.Large]: `height: 56px;`,
  [MdsItemListSize.XLarge]: `min-height: 70px;`,
  [MdsItemListSize.Full]: `min-height: 70px;`,
};
